import {
  DEFAULT_MILESTONE_COUNT,
  DEFAULT_MILESTONES,
  FINAL_AMOUNT_SOURCE,
} from '../../../domain/work.constants';

/** Core project identity — persisted on work_jobs. */
export const basicDetailsDefaults = {
  projectName: '',
  projectDate: null,
  siteAddress: '',
  locality: '',
  basicDetailsNotes: '',
};

/**
 * Secondary attributes — persisted on work_jobs2 (V6) and sent as the API request's
 * nested `details` block. Grouped here under one `details` branch so the form tree
 * mirrors the request: everything outside it belongs to work_jobs.
 *
 * The fields live in three different steps (Sanction & Quotation, Type of Work,
 * Accounting), so per-step validation is wired up in `constants/stepFieldMap.ts`.
 */
export const detailsDefaults = {
  details: {
    documentReferenceNumber: '',
    sanction: { number: '', date: null },
    sanctioningAuthority: { type: '', townPlanningOffice: '', taluka: '' },
    quotation: { issuedBy: '', signedBy: '' },
    alliedWorkDetails: '',
    accountingNotes: '',
  },
};

/**
 * Points of contact. `id` holds the row this section refers to in the matching
 * `/api/poc/*` master — the create endpoint links contacts by `architect_id`,
 * `builder_id`, `owner_id`, `client_id` and `supervisor_id`, not by inline details.
 * Null means "not chosen from the directory": the row is created on submit from
 * whatever was typed here.
 */
export const pocDefaults = {
  architect: {
    id: null,
    name: '',
    phone: '',
    mobile: '',
    whatsapp: '',
    email: '',
    officeAddress: '',
  },
  builder: {
    id: null,
    name: '',
    phone: '',
    mobile: '',
    whatsapp: '',
    email: '',
    officeAddress: '',
  },
  owner: {
    id: null,
    name: '',
    phone: '',
    mobile: '',
    whatsapp: '',
    email: '',
    officeAddress: '',
    buildingName: '',
    supervisorId: null,
    supervisorName: '',
    supervisorContact: '',
  },
  client: {
    id: null,
    name: '',
    phone: '',
    mobile: '',
    whatsapp: '',
    email: '',
    officeAddress: '',
    companyName: '',
    gstNumber: '',
    workOrderRequired: false,
    workOrderNumber: '',
  },
  pocNotes: '',
};

export const typeOfWorkDefaults = {
  workAge: '',
  workCategory: '',

  hasAlliedWork: false,
  hasBuildingWork: false,

  buildingType: '',
  buildingTypeOther: '',

  hasBasement: null,
  basementCount: '',

  groundLevelType: '',
  singleGroundUsage: '',
  singleHasMezzanine: null,

  lowerGroundRequiresColumnFooting: true,
  lowerGroundUsage: '',
  lowerGroundHasMezzanine: null,

  upperGroundUsage: '',
  upperGroundHasMezzanine: null,

  upperFloorsCount: '',
  upperFloorGroups: [{ name: '', floors: [] }],

  hasAdditionalFloors: null,
  additionalFloorsCount: '',

  /**
   * "Work Completed Upto": the highest floor already built, on modification/extension/
   * addition jobs. Floors 1 to this one, the ground level and any basement are existing, and
   * the new work starts on the floor above. Stored as `starting_floor_no` (this plus one),
   * which is what it replaced on screen; the mappers convert both ways.
   */
  workCompletedUpto: '',

  /**
   * Staircase cap / OHWT / LMR. Yes with one of them is the common case, so that is where
   * the form starts; each one is its own slab, so the count adds to the structural drawings.
   */
  hasStaircaseOhwt: true,
  staircaseOhwtCount: '1',

  /** Built-up area for the whole job. Saved on the job, and the quotation reads it back. */
  totalAreaSqFt: '',

  slabsCount: '',

  __upperFloorAssignmentCheck: '',
  typeOfWorkNotes: '',
};

export const accountingDefaults = {
  quote: {
    asPer: '',
    area: '',
    lumpsum: { value: '' },
    sqft: { rate: '', finalSource: FINAL_AMOUNT_SOURCE.CALCULATED, finalValue: '' },
    percentage: {
      constructionRate: '',
      constructionCostManual: '',
      value: '',
      finalSource: FINAL_AMOUNT_SOURCE.CALCULATED,
      finalValue: '',
    },
    /**
     * What section B of the quotation PDF says is being quoted — the lump-sum item list,
     * or the floors a sq. ft. rate covers. Client-facing, unlike `details.accountingNotes`,
     * which is the internal note and never reaches the letter.
     */
    scopeDescription: '',
  },
  siteVisit: {
    /**
     * Whether site visits are in scope at all. Off makes the letter's site-visit note read
     * "Site Visit- Not in Scope"; a zero charge cannot say that, because a quotation may
     * charge nothing per visit and still include them.
     */
    inScope: true,
    /**
     * V25 — 'INCLUDING' or 'EXCLUDING' all expenses. Asked while visits are in scope; it picks
     * the note the quotation prints under the per-visit rates.
     */
    expenses: '',
    engineer: { charge: '', freeVisits: '' },
    director: { charge: '', freeVisits: '' },
  },
  discount: { value: '' },
  milestones: {
    count: DEFAULT_MILESTONE_COUNT,
    // The firm's standard stages, pre-filled and still editable.
    items: DEFAULT_MILESTONES.map((milestone) => ({ ...milestone })),
  },
  jobCode: '',
};

export const defaultValues = {
  ...basicDetailsDefaults,
  ...detailsDefaults,
  ...typeOfWorkDefaults,
  ...pocDefaults,
  ...accountingDefaults,
};
